/**
 * Catalogo ANAC c1 delle denominazioni dei nodi — FILE GENERATO.
 *
 * Non modificare a mano: rigenerare con `npm run catalogo`, che lo ricava da
 * `seed/albero-anac-c1.json`. Le denominazioni sono normativamente vincolate e
 * la difformita' e' rilevata automaticamente da TrasparenzAI: nella UI si
 * scelgono da select, mai da campo libero (CONVENTIONS §7.8).
 *
 * Sorgente : https://guida-servizi.anticorruzione.it/it/help/trasparenza/albero/c1
 * Ambito   : c1
 * Versione : 2.1.0
 */

/** Voce del catalogo ufficiale: una denominazione selezionabile. */
export interface VoceCatalogo {
  readonly id_nodo: string;
  readonly livello: 1 | 2 | 3;
  readonly id_padre: string | null;
  readonly denominazione: string;
  readonly riferimento_normativo: string;
}

/**
 * Unico ramo in cui ANAC consente denominazioni libere: «Altri contenuti →
 * Dati ulteriori». Ovunque altrove il campo denominazione e' una select sul
 * catalogo.
 *
 * E' la sottosezione, **non** la sezione: un nodo libero appeso direttamente ad
 * «Altri contenuti» sarebbe una quinta sottosezione di secondo livello dove
 * l'albero ANAC ne prevede quattro, cioe' proprio la difformita' che
 * TrasparenzAI confronta stringa per stringa. Con due soli livelli quella era
 * l'unica collocazione disponibile; con il terzo livello la collocazione giusta
 * esiste.
 *
 * Deve coincidere con `PADRI_DENOMINAZIONE_LIBERA` di
 * `api/app/models/catalogo_anac.py`, che e' la costante applicata davvero in
 * scrittura: aprire il campo dove l'API lo rifiuta significa far compilare la
 * modale all'operatore per poi restituirgli un 422. L'allineamento fra le due
 * copie e' asserito da `verifiche/dominio.verifica.ts`.
 */
export const RAMO_DENOMINAZIONI_LIBERE = "dati_ulteriori";

/**
 * Dicitura da esporre quando un obbligo non e' applicabile all'ente.
 *
 * Il testo e' vincolato e va riprodotto alla lettera, accenti compresi: e'
 * confrontato dalle verifiche automatiche. La stessa costante vive in
 * `api/app/models/catalogo_anac.py` e in `portale/app.py`, e le tre copie
 * devono restare identiche carattere per carattere.
 */
export const DICITURA_NON_APPLICABILE =
  "L'obbligo di pubblicazione non è applicabile all'amministrazione";

export const CATALOGO_NODI: readonly VoceCatalogo[] = [
  {
    id_nodo: "disposizioni_generali",
    livello: 1,
    id_padre: null,
    denominazione: "Disposizioni generali",
    riferimento_normativo: "d.lgs. 33/2013, artt. 10, 12, 34",
  },
  {
    id_nodo: "piano_triennale_prevenzione_corruzione_trasparenza",
    livello: 2,
    id_padre: "disposizioni_generali",
    denominazione: "Piano triennale per la prevenzione della corruzione e della trasparenza",
    riferimento_normativo: "d.lgs. 33/2013, art. 10, c. 8, lett. a)",
  },
  {
    id_nodo: "piano_triennale_prevenzione_corruzione_trasparenza_ptpct",
    livello: 3,
    id_padre: "piano_triennale_prevenzione_corruzione_trasparenza",
    denominazione: "Piano triennale per la prevenzione della corruzione e della trasparenza (PTPCT)",
    riferimento_normativo: "",
  },
  {
    id_nodo: "atti_generali",
    livello: 2,
    id_padre: "disposizioni_generali",
    denominazione: "Atti generali",
    riferimento_normativo: "d.lgs. 33/2013, art. 12, c. 1, 2",
  },
  {
    id_nodo: "riferimenti_normativi_organizzazione_attivita",
    livello: 3,
    id_padre: "atti_generali",
    denominazione: "Riferimenti normativi su organizzazione e attività",
    riferimento_normativo: "",
  },
  {
    id_nodo: "atti_amministrativi_generali",
    livello: 3,
    id_padre: "atti_generali",
    denominazione: "Atti amministrativi generali",
    riferimento_normativo: "",
  },
  {
    id_nodo: "documenti_programmazione_strategico_gestionale",
    livello: 3,
    id_padre: "atti_generali",
    denominazione: "Documenti di programmazione strategico-gestionale",
    riferimento_normativo: "",
  },
  {
    id_nodo: "statuti_leggi_regionali",
    livello: 3,
    id_padre: "atti_generali",
    denominazione: "Statuti e leggi regionali",
    riferimento_normativo: "",
  },
  {
    id_nodo: "codice_disciplinare_codice_condotta",
    livello: 3,
    id_padre: "atti_generali",
    denominazione: "Codice disciplinare e codice di condotta",
    riferimento_normativo: "",
  },
  {
    id_nodo: "oneri_informativi_cittadini_imprese",
    livello: 2,
    id_padre: "disposizioni_generali",
    denominazione: "Oneri informativi per cittadini e imprese",
    riferimento_normativo: "d.lgs. 33/2013, art. 34, c. 1, 2",
  },
  {
    id_nodo: "scadenzario_obblighi_amministrativi",
    livello: 3,
    id_padre: "oneri_informativi_cittadini_imprese",
    denominazione: "Scadenzario obblighi amministrativi",
    riferimento_normativo: "",
  },
  {
    id_nodo: "oneri_informativi_cittadini_imprese_2",
    livello: 3,
    id_padre: "oneri_informativi_cittadini_imprese",
    denominazione: "Oneri informativi per cittadini e imprese",
    riferimento_normativo: "",
  },
  {
    id_nodo: "burocrazia_zero",
    livello: 2,
    id_padre: "disposizioni_generali",
    denominazione: "Burocrazia zero",
    riferimento_normativo: "",
  },
  {
    id_nodo: "burocrazia_zero_2",
    livello: 3,
    id_padre: "burocrazia_zero",
    denominazione: "Burocrazia zero",
    riferimento_normativo: "",
  },
  {
    id_nodo: "attivita_soggette_controllo",
    livello: 3,
    id_padre: "burocrazia_zero",
    denominazione: "Attività soggette a controllo",
    riferimento_normativo: "",
  },
  {
    id_nodo: "organizzazione",
    livello: 1,
    id_padre: null,
    denominazione: "Organizzazione",
    riferimento_normativo: "d.lgs. 33/2013, artt. 13, 14, 28, 47",
  },
  {
    id_nodo: "titolari_incarichi_politici_amministrazione_direzione_governo",
    livello: 2,
    id_padre: "organizzazione",
    denominazione: "Titolari di incarichi politici, di amministrazione, di direzione o di governo",
    riferimento_normativo: "d.lgs. 33/2013, art. 13, c. 1, lett. a); art. 14",
  },
  {
    id_nodo: "titolari_incarichi_politici",
    livello: 3,
    id_padre: "titolari_incarichi_politici_amministrazione_direzione_governo",
    denominazione: "Titolari di incarichi politici",
    riferimento_normativo: "",
  },
  {
    id_nodo: "titolari_incarichi_amministrazione_direzione_governo",
    livello: 3,
    id_padre: "titolari_incarichi_politici_amministrazione_direzione_governo",
    denominazione: "Titolari di incarichi di amministrazione, di direzione o di governo",
    riferimento_normativo: "",
  },
  {
    id_nodo: "cessati_dall_incarico",
    livello: 3,
    id_padre: "titolari_incarichi_politici_amministrazione_direzione_governo",
    denominazione: "Cessati dall'incarico",
    riferimento_normativo: "",
  },
  {
    id_nodo: "sanzioni_mancata_comunicazione_dati_organizzazione",
    livello: 2,
    id_padre: "organizzazione",
    denominazione: "Sanzioni per mancata comunicazione dei dati",
    riferimento_normativo: "d.lgs. 33/2013, art. 47",
  },
  {
    id_nodo: "sanzioni_mancata_incompleta_comunicazione_dati",
    livello: 3,
    id_padre: "sanzioni_mancata_comunicazione_dati_organizzazione",
    denominazione: "Sanzioni per mancata o incompleta comunicazione dei dati",
    riferimento_normativo: "",
  },
  {
    id_nodo: "rendiconti_gruppi_consiliari_regionali_provinciali",
    livello: 2,
    id_padre: "organizzazione",
    denominazione: "Rendiconti gruppi consiliari regionali/provinciali",
    riferimento_normativo: "d.lgs. 33/2013, art. 28, c. 1",
  },
  {
    id_nodo: "rendiconti_gruppi_consiliari_regionali_provinciali_2",
    livello: 3,
    id_padre: "rendiconti_gruppi_consiliari_regionali_provinciali",
    denominazione: "Rendiconti gruppi consiliari regionali/provinciali",
    riferimento_normativo: "",
  },
  {
    id_nodo: "atti_organi_controllo",
    livello: 3,
    id_padre: "rendiconti_gruppi_consiliari_regionali_provinciali",
    denominazione: "Atti degli organi di controllo",
    riferimento_normativo: "",
  },
  {
    id_nodo: "articolazione_uffici",
    livello: 2,
    id_padre: "organizzazione",
    denominazione: "Articolazione degli uffici",
    riferimento_normativo: "d.lgs. 33/2013, art. 13, c. 1, lett. b), c)",
  },
  {
    id_nodo: "articolazione_uffici_2",
    livello: 3,
    id_padre: "articolazione_uffici",
    denominazione: "Articolazione degli uffici",
    riferimento_normativo: "",
  },
  {
    id_nodo: "organigramma",
    livello: 3,
    id_padre: "articolazione_uffici",
    denominazione: "Organigramma",
    riferimento_normativo: "",
  },
  {
    id_nodo: "telefono_posta_elettronica",
    livello: 2,
    id_padre: "organizzazione",
    denominazione: "Telefono e posta elettronica",
    riferimento_normativo: "d.lgs. 33/2013, art. 13, c. 1, lett. d)",
  },
  {
    id_nodo: "telefono_posta_elettronica_2",
    livello: 3,
    id_padre: "telefono_posta_elettronica",
    denominazione: "Telefono e posta elettronica",
    riferimento_normativo: "",
  },
  {
    id_nodo: "consulenti_collaboratori",
    livello: 1,
    id_padre: null,
    denominazione: "Consulenti e collaboratori",
    riferimento_normativo: "d.lgs. 33/2013, art. 15",
  },
  {
    id_nodo: "titolari_incarichi_collaborazione_consulenza",
    livello: 2,
    id_padre: "consulenti_collaboratori",
    denominazione: "Titolari di incarichi di collaborazione o consulenza",
    riferimento_normativo: "d.lgs. 33/2013, art. 15, c. 1, 2",
  },
  {
    id_nodo: "consulenti_collaboratori_titolari_incarichi",
    livello: 3,
    id_padre: "titolari_incarichi_collaborazione_consulenza",
    denominazione: "Consulenti e collaboratori",
    riferimento_normativo: "",
  },
  {
    id_nodo: "personale",
    livello: 1,
    id_padre: null,
    denominazione: "Personale",
    riferimento_normativo: "d.lgs. 33/2013, artt. 14, 16, 17, 18, 21, 47",
  },
  {
    id_nodo: "titolari_incarichi_dirigenziali_amministrativi_vertice",
    livello: 2,
    id_padre: "personale",
    denominazione: "Titolari di incarichi dirigenziali amministrativi di vertice",
    riferimento_normativo: "d.lgs. 33/2013, art. 14, c. 1, 1-bis",
  },
  {
    id_nodo: "incarichi_amministrativi_vertice",
    livello: 3,
    id_padre: "titolari_incarichi_dirigenziali_amministrativi_vertice",
    denominazione: "Incarichi amministrativi di vertice",
    riferimento_normativo: "",
  },
  {
    id_nodo: "titolari_incarichi_dirigenziali",
    livello: 2,
    id_padre: "personale",
    denominazione: "Titolari di incarichi dirigenziali",
    riferimento_normativo: "d.lgs. 33/2013, art. 14, c. 1, 1-bis",
  },
  {
    id_nodo: "incarichi_dirigenziali",
    livello: 3,
    id_padre: "titolari_incarichi_dirigenziali",
    denominazione: "Incarichi dirigenziali",
    riferimento_normativo: "",
  },
  {
    id_nodo: "elenco_posizioni_dirigenziali_discrezionali",
    livello: 3,
    id_padre: "titolari_incarichi_dirigenziali",
    denominazione: "Elenco posizioni dirigenziali discrezionali",
    riferimento_normativo: "",
  },
  {
    id_nodo: "posti_funzione_disponibili",
    livello: 3,
    id_padre: "titolari_incarichi_dirigenziali",
    denominazione: "Posti di funzione disponibili",
    riferimento_normativo: "",
  },
  {
    id_nodo: "ruolo_dirigenti",
    livello: 3,
    id_padre: "titolari_incarichi_dirigenziali",
    denominazione: "Ruolo dirigenti",
    riferimento_normativo: "",
  },
  {
    id_nodo: "dirigenti_cessati",
    livello: 2,
    id_padre: "personale",
    denominazione: "Dirigenti cessati",
    riferimento_normativo: "d.lgs. 33/2013, art. 14, c. 1, lett. a), b), c), d), e), f)",
  },
  {
    id_nodo: "dirigenti_cessati_rapporto_lavoro",
    livello: 3,
    id_padre: "dirigenti_cessati",
    denominazione: "Dirigenti cessati dal rapporto di lavoro",
    riferimento_normativo: "",
  },
  {
    id_nodo: "sanzioni_mancata_comunicazione_dati_personale",
    livello: 2,
    id_padre: "personale",
    denominazione: "Sanzioni per mancata comunicazione dei dati",
    riferimento_normativo: "d.lgs. 33/2013, art. 47, c. 1",
  },
  {
    id_nodo: "sanzioni_mancata_incompleta_comunicazione_dati_sanzioni_mancata",
    livello: 3,
    id_padre: "sanzioni_mancata_comunicazione_dati_personale",
    denominazione: "Sanzioni per mancata o incompleta comunicazione dei dati",
    riferimento_normativo: "",
  },
  {
    id_nodo: "posizioni_organizzative",
    livello: 2,
    id_padre: "personale",
    denominazione: "Posizioni organizzative",
    riferimento_normativo: "d.lgs. 33/2013, art. 14, c. 1-quinquies",
  },
  {
    id_nodo: "posizioni_organizzative_2",
    livello: 3,
    id_padre: "posizioni_organizzative",
    denominazione: "Posizioni organizzative",
    riferimento_normativo: "",
  },
  {
    id_nodo: "dotazione_organica",
    livello: 2,
    id_padre: "personale",
    denominazione: "Dotazione organica",
    riferimento_normativo: "d.lgs. 33/2013, art. 16, c. 1, 2",
  },
  {
    id_nodo: "conto_annuale_personale",
    livello: 3,
    id_padre: "dotazione_organica",
    denominazione: "Conto annuale del personale",
    riferimento_normativo: "",
  },
  {
    id_nodo: "costo_personale_tempo_indeterminato",
    livello: 3,
    id_padre: "dotazione_organica",
    denominazione: "Costo personale tempo indeterminato",
    riferimento_normativo: "",
  },
  {
    id_nodo: "personale_non_tempo_indeterminato",
    livello: 2,
    id_padre: "personale",
    denominazione: "Personale non a tempo indeterminato",
    riferimento_normativo: "d.lgs. 33/2013, art. 17, c. 1, 2",
  },
  {
    id_nodo: "personale_non_tempo_indeterminato_2",
    livello: 3,
    id_padre: "personale_non_tempo_indeterminato",
    denominazione: "Personale non a tempo indeterminato",
    riferimento_normativo: "",
  },
  {
    id_nodo: "costo_personale_non_tempo_indeterminato",
    livello: 3,
    id_padre: "personale_non_tempo_indeterminato",
    denominazione: "Costo del personale non a tempo indeterminato",
    riferimento_normativo: "",
  },
  {
    id_nodo: "tassi_assenza",
    livello: 2,
    id_padre: "personale",
    denominazione: "Tassi di assenza",
    riferimento_normativo: "d.lgs. 33/2013, art. 16, c. 3",
  },
  {
    id_nodo: "tassi_assenza_trimestrali",
    livello: 3,
    id_padre: "tassi_assenza",
    denominazione: "Tassi di assenza trimestrali",
    riferimento_normativo: "",
  },
  {
    id_nodo: "incarichi_conferiti_autorizzati_dipendenti",
    livello: 2,
    id_padre: "personale",
    denominazione: "Incarichi conferiti e autorizzati ai dipendenti",
    riferimento_normativo: "d.lgs. 33/2013, art. 18",
  },
  {
    id_nodo: "incarichi_conferiti_autorizzati_dipendenti_2",
    livello: 3,
    id_padre: "incarichi_conferiti_autorizzati_dipendenti",
    denominazione: "Incarichi conferiti e autorizzati ai dipendenti",
    riferimento_normativo: "",
  },
  {
    id_nodo: "contrattazione_collettiva",
    livello: 2,
    id_padre: "personale",
    denominazione: "Contrattazione collettiva",
    riferimento_normativo: "d.lgs. 33/2013, art. 21, c. 1",
  },
  {
    id_nodo: "contrattazione_collettiva_2",
    livello: 3,
    id_padre: "contrattazione_collettiva",
    denominazione: "Contrattazione collettiva",
    riferimento_normativo: "",
  },
  {
    id_nodo: "contrattazione_integrativa",
    livello: 2,
    id_padre: "personale",
    denominazione: "Contrattazione integrativa",
    riferimento_normativo: "d.lgs. 33/2013, art. 21, c. 2",
  },
  {
    id_nodo: "contratti_integrativi",
    livello: 3,
    id_padre: "contrattazione_integrativa",
    denominazione: "Contratti integrativi",
    riferimento_normativo: "",
  },
  {
    id_nodo: "costi_contratti_integrativi",
    livello: 3,
    id_padre: "contrattazione_integrativa",
    denominazione: "Costi contratti integrativi",
    riferimento_normativo: "",
  },
  {
    id_nodo: "oiv",
    livello: 2,
    id_padre: "personale",
    denominazione: "OIV",
    riferimento_normativo: "d.lgs. 33/2013, art. 10, c. 8, lett. c)",
  },
  {
    id_nodo: "oiv_2",
    livello: 3,
    id_padre: "oiv",
    denominazione: "OIV",
    riferimento_normativo: "",
  },
  {
    id_nodo: "bandi_concorso",
    livello: 1,
    id_padre: null,
    denominazione: "Bandi di concorso",
    riferimento_normativo: "d.lgs. 33/2013, art. 19",
  },
  {
    id_nodo: "bandi_concorso_2",
    livello: 2,
    id_padre: "bandi_concorso",
    denominazione: "Bandi di concorso",
    riferimento_normativo: "",
  },
  {
    id_nodo: "bandi_concorso_3",
    livello: 3,
    id_padre: "bandi_concorso_2",
    denominazione: "Bandi di concorso",
    riferimento_normativo: "",
  },
  {
    id_nodo: "performance",
    livello: 1,
    id_padre: null,
    denominazione: "Performance",
    riferimento_normativo: "d.lgs. 33/2013, artt. 10, 20",
  },
  {
    id_nodo: "sistema_misurazione_valutazione_performance",
    livello: 2,
    id_padre: "performance",
    denominazione: "Sistema di misurazione e valutazione della Performance",
    riferimento_normativo: "d.lgs. 150/2009, art. 7; delibera CiVIT 104/2010, par. 1",
  },
  {
    id_nodo: "sistema_misurazione_valutazione_performance_2",
    livello: 3,
    id_padre: "sistema_misurazione_valutazione_performance",
    denominazione: "Sistema di misurazione e valutazione della Performance",
    riferimento_normativo: "",
  },
  {
    id_nodo: "piano_performance",
    livello: 2,
    id_padre: "performance",
    denominazione: "Piano della Performance",
    riferimento_normativo: "d.lgs. 33/2013, art. 10, c. 8, lett. b)",
  },
  {
    id_nodo: "piano_performance_2",
    livello: 3,
    id_padre: "piano_performance",
    denominazione: "Piano della Performance",
    riferimento_normativo: "",
  },
  {
    id_nodo: "relazione_performance",
    livello: 2,
    id_padre: "performance",
    denominazione: "Relazione sulla Performance",
    riferimento_normativo: "d.lgs. 33/2013, art. 10, c. 8, lett. b)",
  },
  {
    id_nodo: "relazione_performance_2",
    livello: 3,
    id_padre: "relazione_performance",
    denominazione: "Relazione sulla Performance",
    riferimento_normativo: "",
  },
  {
    id_nodo: "ammontare_complessivo_premi",
    livello: 2,
    id_padre: "performance",
    denominazione: "Ammontare complessivo dei premi",
    riferimento_normativo: "d.lgs. 33/2013, art. 20, c. 1",
  },
  {
    id_nodo: "ammontare_complessivo_premi_2",
    livello: 3,
    id_padre: "ammontare_complessivo_premi",
    denominazione: "Ammontare complessivo dei premi",
    riferimento_normativo: "",
  },
  {
    id_nodo: "dati_relativi_premi",
    livello: 2,
    id_padre: "performance",
    denominazione: "Dati relativi ai premi",
    riferimento_normativo: "d.lgs. 33/2013, art. 20, c. 2",
  },
  {
    id_nodo: "dati_relativi_premi_2",
    livello: 3,
    id_padre: "dati_relativi_premi",
    denominazione: "Dati relativi ai premi",
    riferimento_normativo: "",
  },
  {
    id_nodo: "benessere_organizzativo",
    livello: 2,
    id_padre: "performance",
    denominazione: "Benessere organizzativo",
    riferimento_normativo: "",
  },
  {
    id_nodo: "benessere_organizzativo_2",
    livello: 3,
    id_padre: "benessere_organizzativo",
    denominazione: "Benessere organizzativo",
    riferimento_normativo: "",
  },
  {
    id_nodo: "enti_controllati",
    livello: 1,
    id_padre: null,
    denominazione: "Enti controllati",
    riferimento_normativo: "d.lgs. 33/2013, art. 22",
  },
  {
    id_nodo: "enti_pubblici_vigilati",
    livello: 2,
    id_padre: "enti_controllati",
    denominazione: "Enti pubblici vigilati",
    riferimento_normativo: "d.lgs. 33/2013, art. 22, c. 1, lett. a), c. 2, 3",
  },
  {
    id_nodo: "enti_pubblici_vigilati_2",
    livello: 3,
    id_padre: "enti_pubblici_vigilati",
    denominazione: "Enti pubblici vigilati",
    riferimento_normativo: "",
  },
  {
    id_nodo: "societa_partecipate",
    livello: 2,
    id_padre: "enti_controllati",
    denominazione: "Società partecipate",
    riferimento_normativo: "d.lgs. 33/2013, art. 22, c. 1, lett. b), c. 2, 3",
  },
  {
    id_nodo: "dati_societa_partecipate",
    livello: 3,
    id_padre: "societa_partecipate",
    denominazione: "Dati società partecipate",
    riferimento_normativo: "",
  },
  {
    id_nodo: "provvedimenti_societa_partecipate",
    livello: 3,
    id_padre: "societa_partecipate",
    denominazione: "Provvedimenti",
    riferimento_normativo: "",
  },
  {
    id_nodo: "enti_diritto_privato_controllati",
    livello: 2,
    id_padre: "enti_controllati",
    denominazione: "Enti di diritto privato controllati",
    riferimento_normativo: "d.lgs. 33/2013, art. 22, c. 1, lett. c), c. 2, 3",
  },
  {
    id_nodo: "enti_diritto_privato_controllati_2",
    livello: 3,
    id_padre: "enti_diritto_privato_controllati",
    denominazione: "Enti di diritto privato controllati",
    riferimento_normativo: "",
  },
  {
    id_nodo: "rappresentazione_grafica",
    livello: 2,
    id_padre: "enti_controllati",
    denominazione: "Rappresentazione grafica",
    riferimento_normativo: "d.lgs. 33/2013, art. 22, c. 1, lett. d)",
  },
  {
    id_nodo: "rappresentazione_grafica_2",
    livello: 3,
    id_padre: "rappresentazione_grafica",
    denominazione: "Rappresentazione grafica",
    riferimento_normativo: "",
  },
  {
    id_nodo: "attivita_procedimenti",
    livello: 1,
    id_padre: null,
    denominazione: "Attività e procedimenti",
    riferimento_normativo: "d.lgs. 33/2013, art. 35",
  },
  {
    id_nodo: "dati_aggregati_attivita_amministrativa",
    livello: 2,
    id_padre: "attivita_procedimenti",
    denominazione: "Dati aggregati attività amministrativa",
    riferimento_normativo: "",
  },
  {
    id_nodo: "dati_aggregati_attivita_amministrativa_2",
    livello: 3,
    id_padre: "dati_aggregati_attivita_amministrativa",
    denominazione: "Dati aggregati attività amministrativa",
    riferimento_normativo: "",
  },
  {
    id_nodo: "tipologie_procedimento",
    livello: 2,
    id_padre: "attivita_procedimenti",
    denominazione: "Tipologie di procedimento",
    riferimento_normativo: "d.lgs. 33/2013, art. 35, c. 1, 2",
  },
  {
    id_nodo: "tipologie_procedimento_2",
    livello: 3,
    id_padre: "tipologie_procedimento",
    denominazione: "Tipologie di procedimento",
    riferimento_normativo: "",
  },
  {
    id_nodo: "monitoraggio_tempi_procedimentali",
    livello: 2,
    id_padre: "attivita_procedimenti",
    denominazione: "Monitoraggio tempi procedimentali",
    riferimento_normativo: "",
  },
  {
    id_nodo: "monitoraggio_tempi_procedimentali_2",
    livello: 3,
    id_padre: "monitoraggio_tempi_procedimentali",
    denominazione: "Monitoraggio tempi procedimentali",
    riferimento_normativo: "",
  },
  {
    id_nodo: "dichiarazioni_sostitutive_acquisizione_ufficio_dati",
    livello: 2,
    id_padre: "attivita_procedimenti",
    denominazione: "Dichiarazioni sostitutive e acquisizione d'ufficio dei dati",
    riferimento_normativo: "d.lgs. 33/2013, art. 35, c. 3",
  },
  {
    id_nodo: "recapiti_dell_ufficio_responsabile",
    livello: 3,
    id_padre: "dichiarazioni_sostitutive_acquisizione_ufficio_dati",
    denominazione: "Recapiti dell'ufficio responsabile",
    riferimento_normativo: "",
  },
  {
    id_nodo: "provvedimenti",
    livello: 1,
    id_padre: null,
    denominazione: "Provvedimenti",
    riferimento_normativo: "d.lgs. 33/2013, art. 23",
  },
  {
    id_nodo: "provvedimenti_organi_indirizzo_politico",
    livello: 2,
    id_padre: "provvedimenti",
    denominazione: "Provvedimenti organi indirizzo politico",
    riferimento_normativo: "d.lgs. 33/2013, art. 23, c. 1",
  },
  {
    id_nodo: "provvedimenti_organi_indirizzo_politico_2",
    livello: 3,
    id_padre: "provvedimenti_organi_indirizzo_politico",
    denominazione: "Provvedimenti organi indirizzo politico",
    riferimento_normativo: "",
  },
  {
    id_nodo: "provvedimenti_dirigenti_amministrativi",
    livello: 2,
    id_padre: "provvedimenti",
    denominazione: "Provvedimenti dirigenti amministrativi",
    riferimento_normativo: "d.lgs. 33/2013, art. 23, c. 1",
  },
  {
    id_nodo: "provvedimenti_dirigenti_amministrativi_2",
    livello: 3,
    id_padre: "provvedimenti_dirigenti_amministrativi",
    denominazione: "Provvedimenti dirigenti amministrativi",
    riferimento_normativo: "",
  },
  {
    id_nodo: "controlli_imprese",
    livello: 1,
    id_padre: null,
    denominazione: "Controlli sulle imprese",
    riferimento_normativo: "d.lgs. 33/2013, art. 25",
  },
  {
    id_nodo: "controlli_imprese_2",
    livello: 2,
    id_padre: "controlli_imprese",
    denominazione: "Controlli sulle imprese",
    riferimento_normativo: "d.lgs. 33/2013, art. 25",
  },
  {
    id_nodo: "tipologie_controllo",
    livello: 3,
    id_padre: "controlli_imprese_2",
    denominazione: "Tipologie di controllo",
    riferimento_normativo: "",
  },
  {
    id_nodo: "obblighi_adempimenti",
    livello: 3,
    id_padre: "controlli_imprese_2",
    denominazione: "Obblighi e adempimenti",
    riferimento_normativo: "",
  },
  {
    id_nodo: "bandi_gara_contratti",
    livello: 1,
    id_padre: null,
    denominazione: "Bandi di gara e contratti",
    riferimento_normativo: "d.lgs. 33/2013, art. 37; l. 190/2012, art. 1, c. 32; d.lgs. 36/2023",
  },
  {
    id_nodo: "atti_documenti_carattere_generale_riferiti_tutte",
    livello: 2,
    id_padre: "bandi_gara_contratti",
    denominazione: "Atti e documenti di carattere generale riferiti a tutte le procedure",
    riferimento_normativo: "",
  },
  {
    id_nodo: "elenco_soluzioni_tecnologiche_adottate",
    livello: 3,
    id_padre: "atti_documenti_carattere_generale_riferiti_tutte",
    denominazione: "Elenco delle soluzioni tecnologiche adottate",
    riferimento_normativo: "",
  },
  {
    id_nodo: "avviso_manifestazioni_interesse",
    livello: 3,
    id_padre: "atti_documenti_carattere_generale_riferiti_tutte",
    denominazione: "Avviso per manifestazioni di interesse",
    riferimento_normativo: "",
  },
  {
    id_nodo: "comunicazione_mancata_redazione_programma_triennale_lavori",
    livello: 3,
    id_padre: "atti_documenti_carattere_generale_riferiti_tutte",
    denominazione: "Comunicazione mancata redazione programma triennale lavori pubblici",
    riferimento_normativo: "",
  },
  {
    id_nodo: "comunicazione_mancata_redazione_programma_triennale_acquisti",
    livello: 3,
    id_padre: "atti_documenti_carattere_generale_riferiti_tutte",
    denominazione: "Comunicazione mancata redazione programma triennale acquisti",
    riferimento_normativo: "",
  },
  {
    id_nodo: "atti_recanti_norme_criteri_sistema_qualificazione",
    livello: 3,
    id_padre: "atti_documenti_carattere_generale_riferiti_tutte",
    denominazione: "Atti recanti norme e criteri del sistema di qualificazione",
    riferimento_normativo: "",
  },
  {
    id_nodo: "atti_relativi_gravi_illeciti_professionali",
    livello: 3,
    id_padre: "atti_documenti_carattere_generale_riferiti_tutte",
    denominazione: "Atti relativi ai gravi illeciti professionali",
    riferimento_normativo: "",
  },
  {
    id_nodo: "elenco_annuale_progetti_finanziati",
    livello: 3,
    id_padre: "atti_documenti_carattere_generale_riferiti_tutte",
    denominazione: "Elenco annuale dei progetti finanziati",
    riferimento_normativo: "",
  },
  {
    id_nodo: "pubblicazione",
    livello: 2,
    id_padre: "bandi_gara_contratti",
    denominazione: "Pubblicazione",
    riferimento_normativo: "",
  },
  {
    id_nodo: "pubblicazione_2",
    livello: 3,
    id_padre: "pubblicazione",
    denominazione: "Pubblicazione",
    riferimento_normativo: "",
  },
  {
    id_nodo: "documenti_gara",
    livello: 3,
    id_padre: "pubblicazione",
    denominazione: "Documenti di gara",
    riferimento_normativo: "",
  },
  {
    id_nodo: "affidamento",
    livello: 2,
    id_padre: "bandi_gara_contratti",
    denominazione: "Affidamento",
    riferimento_normativo: "",
  },
  {
    id_nodo: "composizione_commissioni_giudicatrici_cv_componenti",
    livello: 3,
    id_padre: "affidamento",
    denominazione: "Composizione delle commissioni giudicatrici e CV dei componenti",
    riferimento_normativo: "",
  },
  {
    id_nodo: "pari_opportunita_inclusione_lavorativa_nei_contratti",
    livello: 3,
    id_padre: "affidamento",
    denominazione: "Pari opportunità e inclusione lavorativa nei contratti pubblici PNRR e PNC",
    riferimento_normativo: "",
  },
  {
    id_nodo: "procedure_affidamento_servizi_pubblici_locali",
    livello: 3,
    id_padre: "affidamento",
    denominazione: "Procedure di affidamento dei servizi pubblici locali",
    riferimento_normativo: "",
  },
  {
    id_nodo: "esecutiva",
    livello: 2,
    id_padre: "bandi_gara_contratti",
    denominazione: "Esecutiva",
    riferimento_normativo: "",
  },
  {
    id_nodo: "composizione_collegio_consultivo_tecnico",
    livello: 3,
    id_padre: "esecutiva",
    denominazione: "Composizione del Collegio consultivo tecnico",
    riferimento_normativo: "",
  },
  {
    id_nodo: "pari_opportunita_inclusione_lavorativa_nei_contratti_esecutiva",
    livello: 3,
    id_padre: "esecutiva",
    denominazione: "Pari opportunità e inclusione lavorativa nei contratti pubblici PNRR e PNC",
    riferimento_normativo: "",
  },
  {
    id_nodo: "sponsorizzazioni",
    livello: 2,
    id_padre: "bandi_gara_contratti",
    denominazione: "Sponsorizzazioni",
    riferimento_normativo: "",
  },
  {
    id_nodo: "affidamento_contratti_sponsorizzazione",
    livello: 3,
    id_padre: "sponsorizzazioni",
    denominazione: "Affidamento di contratti di sponsorizzazione",
    riferimento_normativo: "",
  },
  {
    id_nodo: "procedure_somma_urgenza_protezione_civile",
    livello: 2,
    id_padre: "bandi_gara_contratti",
    denominazione: "Procedure di somma urgenza e di protezione civile",
    riferimento_normativo: "",
  },
  {
    id_nodo: "atti_documenti_relativi_affidamenti_somma_urgenza",
    livello: 3,
    id_padre: "procedure_somma_urgenza_protezione_civile",
    denominazione: "Atti e documenti relativi agli affidamenti di somma urgenza",
    riferimento_normativo: "",
  },
  {
    id_nodo: "finanza_progetto",
    livello: 2,
    id_padre: "bandi_gara_contratti",
    denominazione: "Finanza di progetto",
    riferimento_normativo: "",
  },
  {
    id_nodo: "provvedimento_conclusivo_procedura_valutazione_proposta_promotore",
    livello: 3,
    id_padre: "finanza_progetto",
    denominazione: "Provvedimento conclusivo della procedura di valutazione della proposta del promotore",
    riferimento_normativo: "",
  },
  {
    id_nodo: "sovvenzioni_contributi_sussidi_vantaggi_economici",
    livello: 1,
    id_padre: null,
    denominazione: "Sovvenzioni, contributi, sussidi, vantaggi economici",
    riferimento_normativo: "d.lgs. 33/2013, artt. 26, 27",
  },
  {
    id_nodo: "criteri_modalita",
    livello: 2,
    id_padre: "sovvenzioni_contributi_sussidi_vantaggi_economici",
    denominazione: "Criteri e modalità",
    riferimento_normativo: "d.lgs. 33/2013, art. 26, c. 1",
  },
  {
    id_nodo: "criteri_modalita_2",
    livello: 3,
    id_padre: "criteri_modalita",
    denominazione: "Criteri e modalità",
    riferimento_normativo: "",
  },
  {
    id_nodo: "atti_concessione",
    livello: 2,
    id_padre: "sovvenzioni_contributi_sussidi_vantaggi_economici",
    denominazione: "Atti di concessione",
    riferimento_normativo: "d.lgs. 33/2013, art. 26, c. 2; art. 27",
  },
  {
    id_nodo: "atti_concessione_2",
    livello: 3,
    id_padre: "atti_concessione",
    denominazione: "Atti di concessione",
    riferimento_normativo: "",
  },
  {
    id_nodo: "bilanci",
    livello: 1,
    id_padre: null,
    denominazione: "Bilanci",
    riferimento_normativo: "d.lgs. 33/2013, art. 29",
  },
  {
    id_nodo: "bilancio_preventivo_consuntivo",
    livello: 2,
    id_padre: "bilanci",
    denominazione: "Bilancio preventivo e consuntivo",
    riferimento_normativo: "d.lgs. 33/2013, art. 29, c. 1, 1-bis",
  },
  {
    id_nodo: "bilancio_preventivo",
    livello: 3,
    id_padre: "bilancio_preventivo_consuntivo",
    denominazione: "Bilancio preventivo",
    riferimento_normativo: "",
  },
  {
    id_nodo: "bilancio_consuntivo",
    livello: 3,
    id_padre: "bilancio_preventivo_consuntivo",
    denominazione: "Bilancio consuntivo",
    riferimento_normativo: "",
  },
  {
    id_nodo: "piano_indicatori_risultati_attesi_bilancio",
    livello: 2,
    id_padre: "bilanci",
    denominazione: "Piano degli indicatori e dei risultati attesi di bilancio",
    riferimento_normativo: "d.lgs. 33/2013, art. 29, c. 2",
  },
  {
    id_nodo: "piano_indicatori_risultati_attesi_bilancio_2",
    livello: 3,
    id_padre: "piano_indicatori_risultati_attesi_bilancio",
    denominazione: "Piano degli indicatori e dei risultati attesi di bilancio",
    riferimento_normativo: "",
  },
  {
    id_nodo: "beni_immobili_gestione_patrimonio",
    livello: 1,
    id_padre: null,
    denominazione: "Beni immobili e gestione patrimonio",
    riferimento_normativo: "d.lgs. 33/2013, art. 30",
  },
  {
    id_nodo: "patrimonio_immobiliare",
    livello: 2,
    id_padre: "beni_immobili_gestione_patrimonio",
    denominazione: "Patrimonio immobiliare",
    riferimento_normativo: "d.lgs. 33/2013, art. 30",
  },
  {
    id_nodo: "patrimonio_immobiliare_2",
    livello: 3,
    id_padre: "patrimonio_immobiliare",
    denominazione: "Patrimonio immobiliare",
    riferimento_normativo: "",
  },
  {
    id_nodo: "canoni_locazione_affitto",
    livello: 2,
    id_padre: "beni_immobili_gestione_patrimonio",
    denominazione: "Canoni di locazione o affitto",
    riferimento_normativo: "d.lgs. 33/2013, art. 30",
  },
  {
    id_nodo: "canoni_locazione_affitto_2",
    livello: 3,
    id_padre: "canoni_locazione_affitto",
    denominazione: "Canoni di locazione o affitto",
    riferimento_normativo: "",
  },
  {
    id_nodo: "controlli_rilievi_amministrazione",
    livello: 1,
    id_padre: null,
    denominazione: "Controlli e rilievi sull'amministrazione",
    riferimento_normativo: "d.lgs. 33/2013, art. 31",
  },
  {
    id_nodo: "organismi_indipendenti_valutazione",
    livello: 2,
    id_padre: "controlli_rilievi_amministrazione",
    denominazione: "Organismi indipendenti di valutazione",
    riferimento_normativo: "d.lgs. 33/2013, art. 31",
  },
  {
    id_nodo: "atti_organismi_indipendenti_valutazione",
    livello: 3,
    id_padre: "organismi_indipendenti_valutazione",
    denominazione: "Atti degli Organismi indipendenti di valutazione",
    riferimento_normativo: "",
  },
  {
    id_nodo: "organi_revisione_amministrativa_contabile",
    livello: 2,
    id_padre: "controlli_rilievi_amministrazione",
    denominazione: "Organi di revisione amministrativa e contabile",
    riferimento_normativo: "d.lgs. 33/2013, art. 31",
  },
  {
    id_nodo: "relazioni_organi_revisione_amministrativa_contabile",
    livello: 3,
    id_padre: "organi_revisione_amministrativa_contabile",
    denominazione: "Relazioni degli organi di revisione amministrativa e contabile",
    riferimento_normativo: "",
  },
  {
    id_nodo: "corte_dei_conti",
    livello: 2,
    id_padre: "controlli_rilievi_amministrazione",
    denominazione: "Corte dei conti",
    riferimento_normativo: "d.lgs. 33/2013, art. 31",
  },
  {
    id_nodo: "rilievi_corte_conti",
    livello: 3,
    id_padre: "corte_dei_conti",
    denominazione: "Rilievi Corte dei conti",
    riferimento_normativo: "",
  },
  {
    id_nodo: "servizi_erogati",
    livello: 1,
    id_padre: null,
    denominazione: "Servizi erogati",
    riferimento_normativo: "d.lgs. 33/2013, art. 32",
  },
  {
    id_nodo: "carta_servizi_standard_qualita",
    livello: 2,
    id_padre: "servizi_erogati",
    denominazione: "Carta dei servizi e standard di qualità",
    riferimento_normativo: "d.lgs. 33/2013, art. 32, c. 1",
  },
  {
    id_nodo: "carta_servizi_standard_qualita_2",
    livello: 3,
    id_padre: "carta_servizi_standard_qualita",
    denominazione: "Carta dei servizi e standard di qualità",
    riferimento_normativo: "",
  },
  {
    id_nodo: "class_action",
    livello: 2,
    id_padre: "servizi_erogati",
    denominazione: "Class action",
    riferimento_normativo: "d.lgs. 198/2009, art. 1, c. 2; art. 4, c. 2, 6",
  },
  {
    id_nodo: "class_action_2",
    livello: 3,
    id_padre: "class_action",
    denominazione: "Class action",
    riferimento_normativo: "",
  },
  {
    id_nodo: "costi_contabilizzati",
    livello: 2,
    id_padre: "servizi_erogati",
    denominazione: "Costi contabilizzati",
    riferimento_normativo: "d.lgs. 33/2013, art. 32, c. 2, lett. a); art. 10, c. 5",
  },
  {
    id_nodo: "costi_contabilizzati_2",
    livello: 3,
    id_padre: "costi_contabilizzati",
    denominazione: "Costi contabilizzati",
    riferimento_normativo: "",
  },
  {
    id_nodo: "liste_attesa",
    livello: 2,
    id_padre: "servizi_erogati",
    denominazione: "Liste di attesa",
    riferimento_normativo: "d.lgs. 33/2013, art. 41, c. 6",
  },
  {
    id_nodo: "liste_attesa_2",
    livello: 3,
    id_padre: "liste_attesa",
    denominazione: "Liste di attesa",
    riferimento_normativo: "",
  },
  {
    id_nodo: "servizi_in_rete",
    livello: 2,
    id_padre: "servizi_erogati",
    denominazione: "Servizi in rete",
    riferimento_normativo: "d.lgs. 82/2005 (CAD), art. 7, c. 3",
  },
  {
    id_nodo: "risultati_indagini_soddisfazione_utenti",
    livello: 3,
    id_padre: "servizi_in_rete",
    denominazione: "Risultati delle indagini sulla soddisfazione degli utenti",
    riferimento_normativo: "",
  },
  {
    id_nodo: "pagamenti_amministrazione",
    livello: 1,
    id_padre: null,
    denominazione: "Pagamenti dell'amministrazione",
    riferimento_normativo: "d.lgs. 33/2013, artt. 4-bis, 33, 36",
  },
  {
    id_nodo: "dati_sui_pagamenti",
    livello: 2,
    id_padre: "pagamenti_amministrazione",
    denominazione: "Dati sui pagamenti",
    riferimento_normativo: "d.lgs. 33/2013, art. 4-bis, c. 2",
  },
  {
    id_nodo: "dati_pagamenti",
    livello: 3,
    id_padre: "dati_sui_pagamenti",
    denominazione: "Dati sui pagamenti",
    riferimento_normativo: "",
  },
  {
    id_nodo: "dati_sui_pagamenti_servizio_sanitario_nazionale",
    livello: 2,
    id_padre: "pagamenti_amministrazione",
    denominazione: "Dati sui pagamenti del servizio sanitario nazionale",
    riferimento_normativo: "d.lgs. 33/2013, art. 41, c. 1-bis",
  },
  {
    id_nodo: "dati_pagamenti_forma_sintetica_aggregata",
    livello: 3,
    id_padre: "dati_sui_pagamenti_servizio_sanitario_nazionale",
    denominazione: "Dati sui pagamenti in forma sintetica e aggregata",
    riferimento_normativo: "",
  },
  {
    id_nodo: "indicatore_tempestivita_pagamenti",
    livello: 2,
    id_padre: "pagamenti_amministrazione",
    denominazione: "Indicatore di tempestività dei pagamenti",
    riferimento_normativo: "d.lgs. 33/2013, art. 33",
  },
  {
    id_nodo: "indicatore_tempestivita_pagamenti_2",
    livello: 3,
    id_padre: "indicatore_tempestivita_pagamenti",
    denominazione: "Indicatore di tempestività dei pagamenti",
    riferimento_normativo: "",
  },
  {
    id_nodo: "ammontare_complessivo_debiti",
    livello: 3,
    id_padre: "indicatore_tempestivita_pagamenti",
    denominazione: "Ammontare complessivo dei debiti",
    riferimento_normativo: "",
  },
  {
    id_nodo: "iban_pagamenti_informatici",
    livello: 2,
    id_padre: "pagamenti_amministrazione",
    denominazione: "IBAN e pagamenti informatici",
    riferimento_normativo: "d.lgs. 33/2013, art. 36; d.lgs. 82/2005 (CAD), art. 5, c. 1",
  },
  {
    id_nodo: "iban_pagamenti_informatici_2",
    livello: 3,
    id_padre: "iban_pagamenti_informatici",
    denominazione: "IBAN e pagamenti informatici",
    riferimento_normativo: "",
  },
  {
    id_nodo: "opere_pubbliche",
    livello: 1,
    id_padre: null,
    denominazione: "Opere pubbliche",
    riferimento_normativo: "d.lgs. 33/2013, art. 38",
  },
  {
    id_nodo: "nuclei_valutazione_verifica_investimenti_pubblici",
    livello: 2,
    id_padre: "opere_pubbliche",
    denominazione: "Nuclei di valutazione e verifica degli investimenti pubblici",
    riferimento_normativo: "d.lgs. 33/2013, art. 38, c. 1",
  },
  {
    id_nodo: "informazioni_relative_nuclei_valutazione_verifica_investimenti",
    livello: 3,
    id_padre: "nuclei_valutazione_verifica_investimenti_pubblici",
    denominazione: "Informazioni relative ai nuclei di valutazione e verifica degli investimenti pubblici",
    riferimento_normativo: "",
  },
  {
    id_nodo: "atti_programmazione_opere_pubbliche",
    livello: 2,
    id_padre: "opere_pubbliche",
    denominazione: "Atti di programmazione delle opere pubbliche",
    riferimento_normativo: "d.lgs. 33/2013, art. 38, c. 2, 2-bis",
  },
  {
    id_nodo: "atti_programmazione_opere_pubbliche_2",
    livello: 3,
    id_padre: "atti_programmazione_opere_pubbliche",
    denominazione: "Atti di programmazione delle opere pubbliche",
    riferimento_normativo: "",
  },
  {
    id_nodo: "tempi_costi_indicatori_realizzazione_opere_pubbliche",
    livello: 2,
    id_padre: "opere_pubbliche",
    denominazione: "Tempi costi e indicatori di realizzazione delle opere pubbliche",
    riferimento_normativo: "d.lgs. 33/2013, art. 38, c. 2",
  },
  {
    id_nodo: "tempi_costi_indicatori_realizzazione_opere_pubbliche_2",
    livello: 3,
    id_padre: "tempi_costi_indicatori_realizzazione_opere_pubbliche",
    denominazione: "Tempi costi e indicatori di realizzazione delle opere pubbliche",
    riferimento_normativo: "",
  },
  {
    id_nodo: "pianificazione_governo_territorio",
    livello: 1,
    id_padre: null,
    denominazione: "Pianificazione e governo del territorio",
    riferimento_normativo: "d.lgs. 33/2013, art. 39",
  },
  {
    id_nodo: "pianificazione_governo_territorio_2",
    livello: 2,
    id_padre: "pianificazione_governo_territorio",
    denominazione: "Pianificazione e governo del territorio",
    riferimento_normativo: "d.lgs. 33/2013, art. 39",
  },
  {
    id_nodo: "pianificazione_governo_territorio_3",
    livello: 3,
    id_padre: "pianificazione_governo_territorio_2",
    denominazione: "Pianificazione e governo del territorio",
    riferimento_normativo: "",
  },
  {
    id_nodo: "informazioni_ambientali",
    livello: 1,
    id_padre: null,
    denominazione: "Informazioni ambientali",
    riferimento_normativo: "d.lgs. 33/2013, art. 40; d.lgs. 195/2005",
  },
  {
    id_nodo: "informazioni_ambientali_2",
    livello: 2,
    id_padre: "informazioni_ambientali",
    denominazione: "Informazioni ambientali",
    riferimento_normativo: "d.lgs. 33/2013, art. 40; d.lgs. 195/2005",
  },
  {
    id_nodo: "stato_ambiente",
    livello: 3,
    id_padre: "informazioni_ambientali_2",
    denominazione: "Stato dell'ambiente",
    riferimento_normativo: "d.lgs. 33/2013, art. 40, c. 2",
  },
  {
    id_nodo: "fattori_inquinanti",
    livello: 3,
    id_padre: "informazioni_ambientali_2",
    denominazione: "Fattori inquinanti",
    riferimento_normativo: "d.lgs. 33/2013, art. 40, c. 2",
  },
  {
    id_nodo: "misure_incidenti_ambiente_analisi_impatto",
    livello: 3,
    id_padre: "informazioni_ambientali_2",
    denominazione: "Misure incidenti sull'ambiente e relative analisi di impatto",
    riferimento_normativo: "d.lgs. 33/2013, art. 40, c. 2",
  },
  {
    id_nodo: "misure_protezione_ambiente_analisi_impatto",
    livello: 3,
    id_padre: "informazioni_ambientali_2",
    denominazione: "Misure a protezione dell'ambiente e relative analisi di impatto",
    riferimento_normativo: "d.lgs. 33/2013, art. 40, c. 2",
  },
  {
    id_nodo: "relazioni_attuazione_legislazione",
    livello: 3,
    id_padre: "informazioni_ambientali_2",
    denominazione: "Relazioni sull'attuazione della legislazione",
    riferimento_normativo: "d.lgs. 33/2013, art. 40, c. 2",
  },
  {
    id_nodo: "stato_salute_sicurezza_umana",
    livello: 3,
    id_padre: "informazioni_ambientali_2",
    denominazione: "Stato della salute e della sicurezza umana",
    riferimento_normativo: "d.lgs. 33/2013, art. 40, c. 2",
  },
  {
    id_nodo: "relazione_sullo_stato_dell_ambiente",
    livello: 3,
    id_padre: "informazioni_ambientali_2",
    denominazione: "Relazione sullo stato dell'ambiente",
    riferimento_normativo: "",
  },
  {
    id_nodo: "strutture_sanitarie_private_accreditate",
    livello: 1,
    id_padre: null,
    denominazione: "Strutture sanitarie private accreditate",
    riferimento_normativo: "d.lgs. 33/2013, art. 41, c. 4",
  },
  {
    id_nodo: "strutture_sanitarie_private_accreditate_2",
    livello: 2,
    id_padre: "strutture_sanitarie_private_accreditate",
    denominazione: "Strutture sanitarie private accreditate",
    riferimento_normativo: "d.lgs. 33/2013, art. 41, c. 4",
  },
  {
    id_nodo: "strutture_sanitarie_private_accreditate_3",
    livello: 3,
    id_padre: "strutture_sanitarie_private_accreditate_2",
    denominazione: "Strutture sanitarie private accreditate",
    riferimento_normativo: "",
  },
  {
    id_nodo: "interventi_straordinari_emergenza",
    livello: 1,
    id_padre: null,
    denominazione: "Interventi straordinari e di emergenza",
    riferimento_normativo: "d.lgs. 33/2013, art. 42",
  },
  {
    id_nodo: "interventi_straordinari_emergenza_2",
    livello: 2,
    id_padre: "interventi_straordinari_emergenza",
    denominazione: "Interventi straordinari e di emergenza",
    riferimento_normativo: "d.lgs. 33/2013, art. 42",
  },
  {
    id_nodo: "interventi_straordinari_emergenza_3",
    livello: 3,
    id_padre: "interventi_straordinari_emergenza_2",
    denominazione: "Interventi straordinari e di emergenza",
    riferimento_normativo: "",
  },
  {
    id_nodo: "altri_contenuti",
    livello: 1,
    id_padre: null,
    denominazione: "Altri contenuti",
    riferimento_normativo: "d.lgs. 33/2013, artt. 5, 7-bis, 9; l. 190/2012",
  },
  {
    id_nodo: "prevenzione_corruzione",
    livello: 2,
    id_padre: "altri_contenuti",
    denominazione: "Prevenzione della Corruzione",
    riferimento_normativo: "l. 190/2012, art. 1, c. 8, 14; d.lgs. 39/2013, art. 20, c. 3",
  },
  {
    id_nodo: "piano_triennale_prevenzione_corruzione_trasparenza_prevenzione_corruzione",
    livello: 3,
    id_padre: "prevenzione_corruzione",
    denominazione: "Piano triennale per la prevenzione della corruzione e della trasparenza",
    riferimento_normativo: "",
  },
  {
    id_nodo: "responsabile_prevenzione_corruzione_trasparenza",
    livello: 3,
    id_padre: "prevenzione_corruzione",
    denominazione: "Responsabile della prevenzione della corruzione e della trasparenza",
    riferimento_normativo: "",
  },
  {
    id_nodo: "regolamenti_prevenzione_repressione_corruzione_dell_illegalita",
    livello: 3,
    id_padre: "prevenzione_corruzione",
    denominazione: "Regolamenti per la prevenzione e la repressione della corruzione e dell'illegalità",
    riferimento_normativo: "",
  },
  {
    id_nodo: "relazione_responsabile_prevenzione_corruzione_trasparenza",
    livello: 3,
    id_padre: "prevenzione_corruzione",
    denominazione: "Relazione del responsabile della prevenzione della corruzione e della trasparenza",
    riferimento_normativo: "",
  },
  {
    id_nodo: "provvedimenti_adottati_dall_anac",
    livello: 3,
    id_padre: "prevenzione_corruzione",
    denominazione: "Provvedimenti adottati dall'ANAC",
    riferimento_normativo: "",
  },
  {
    id_nodo: "atti_accertamento_violazioni",
    livello: 3,
    id_padre: "prevenzione_corruzione",
    denominazione: "Atti di accertamento delle violazioni",
    riferimento_normativo: "",
  },
  {
    id_nodo: "accesso_civico",
    livello: 2,
    id_padre: "altri_contenuti",
    denominazione: "Accesso civico",
    riferimento_normativo: "d.lgs. 33/2013, art. 5, c. 1, 2; art. 5-bis, c. 6",
  },
  {
    id_nodo: "accesso_civico_semplice",
    livello: 3,
    id_padre: "accesso_civico",
    denominazione: "Accesso civico semplice",
    riferimento_normativo: "",
  },
  {
    id_nodo: "accesso_civico_generalizzato",
    livello: 3,
    id_padre: "accesso_civico",
    denominazione: "Accesso civico generalizzato",
    riferimento_normativo: "",
  },
  {
    id_nodo: "registro_accessi",
    livello: 3,
    id_padre: "accesso_civico",
    denominazione: "Registro degli accessi",
    riferimento_normativo: "",
  },
  {
    id_nodo: "accessibilita_catalogo_dati_metadati_banche_dati",
    livello: 2,
    id_padre: "altri_contenuti",
    denominazione: "Accessibilità e Catalogo dei dati, metadati e banche dati",
    riferimento_normativo: "d.lgs. 33/2013, art. 53, c. 1-bis; d.lgs. 82/2005 (CAD), art. 9, c. 7",
  },
  {
    id_nodo: "catalogo_dati_metadati_banche_dati",
    livello: 3,
    id_padre: "accessibilita_catalogo_dati_metadati_banche_dati",
    denominazione: "Catalogo dei dati, metadati e delle banche dati",
    riferimento_normativo: "",
  },
  {
    id_nodo: "regolamenti",
    livello: 3,
    id_padre: "accessibilita_catalogo_dati_metadati_banche_dati",
    denominazione: "Regolamenti",
    riferimento_normativo: "",
  },
  {
    id_nodo: "obiettivi_accessibilita",
    livello: 3,
    id_padre: "accessibilita_catalogo_dati_metadati_banche_dati",
    denominazione: "Obiettivi di accessibilità",
    riferimento_normativo: "",
  },
  {
    id_nodo: "dati_ulteriori",
    livello: 2,
    id_padre: "altri_contenuti",
    denominazione: "Dati ulteriori",
    riferimento_normativo: "d.lgs. 33/2013, art. 7-bis, c. 3; art. 1, c. 9, lett. f) l. 190/2012",
  },
  {
    id_nodo: "dati_ulteriori_2",
    livello: 3,
    id_padre: "dati_ulteriori",
    denominazione: "Dati ulteriori",
    riferimento_normativo: "",
  },
];

/** Voci figlie dirette disponibili sotto un dato nodo, a qualunque livello. */
export function catalogoFigli(idPadre: string): readonly VoceCatalogo[] {
  return CATALOGO_NODI.filter((voce) => voce.id_padre === idPadre);
}

/** Voce di catalogo corrispondente a un identificativo, se esiste. */
export function voceCatalogo(idNodo: string): VoceCatalogo | undefined {
  return CATALOGO_NODI.find((voce) => voce.id_nodo === idNodo);
}
